import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import DatePicker from "react-multi-date-picker";
import { useSelector } from "react-redux";
import { CERTIFICATE_STATUS } from "../../data/NewApplicantsConstants";
import FormInputLayout from "../FormInputLayout";
import { colourStylesFullWidth } from "../../data/Constants";
import PlainButton from "../buttons/PlainButton";
import moment from "moment";
import { REJECTED_REASON, rejected_reasons_label_values, SecretariatReason } from "../../data/RejectedConstants";

function BulkStatusApplicantUpdate({ closePopup, handleSubmit, loading }) {
    const firstSelectedStatus = useSelector((state) => state?.general?.firstSelectedStatus);
    const activeTypeTab = useSelector((state) => state.table.certificateType);
    const initialValues = {
        status: null,
        reason: "",
        cpd_hours: "",
        decision_committee_date: "",
        approval_date: "",
        refund_date: "",
    };

    const validationSchema = Yup.object().shape({
        status: Yup.string().required("Status is required"),
        reason: Yup.string().when("status", {
            is: (val) => ["REJECTED", "KIV"].includes(val),
            then: () => Yup.string().required("Reason is required"),
        }),
        cpd_hours: Yup.number().when("status", {
            is: "APPROVED",
            then: () => Yup.number().required("CPD Hours Granted is required"),
        }),
        decision_committee_date: Yup.date()
            .nullable()
            .when("status", {
                is: "PENDING",
                then: () => Yup.date().required("Committee decision date is required"),
            }),
        approval_date: Yup.date()
            .nullable()
            .when("status", {
                is: "SUCCESSFUL",
                then: () => Yup.date().required("Board Approval Date is required"),
            }),
        other_reason: Yup.string().when("reason", {
            is: REJECTED_REASON.OTHERS_SECRETARIAT_CAN_WRITE_THE_REASON_MANUALLY.toString(),
            then: () => Yup.string().required("Others - secretariat can write the reason manually"),
            otherwise: () => Yup.string(),
        }),
        appeal_board_date: Yup.date()
            .nullable()
            .when("status", {
                is: "REJECTED",
                then: () => Yup.date().required("Appeal Board Date is required"),
            }),
        secretariat_reason: "",
    });
    const getStatusOptions = (statuses) => {
        return Object.entries(statuses).map(([label, value]) => ({
            label: label.replace(/_/g, " "), // Replace underscores with spaces for display
            value,
        }));
    };

    // Function to capitalize the first letter of each word
    const capitalizeWords = (str) =>
        str
            .replace(/_/g, " ") // Replace underscores with spaces
            .toLowerCase() // Convert all to lowercase
            .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word

    // Dynamically create statusOptions from CERTIFICATE_STATUS
    const showAllStatus = Object.entries(CERTIFICATE_STATUS).map(([key, value]) => ({
        label: capitalizeWords(key), // Capitalize each word
        value: value,
    }));

    // Function to get status options based on `firstSelectedStatus`
    const getFilteredOptions = () => {
        switch (firstSelectedStatus) {
            case CERTIFICATE_STATUS.PENDING_FOR_BOARD_DECISION:
                if (activeTypeTab == "3" || activeTypeTab == "4") {
                    return getStatusOptions(CERTIFICATE_STATUS).filter((option) =>
                        [
                            CERTIFICATE_STATUS.ASSESSMENT_SUCCESSFUL,
                            CERTIFICATE_STATUS.ASSESSMENT_FAILED,
                            CERTIFICATE_STATUS.ADDITIONAL_REVIEW_CHANGE_FIELD,
                            CERTIFICATE_STATUS.ADDITIONAL_REVIEW_ASSESSMENT,
                        ].includes(option.value)
                    );
                } else if (activeTypeTab == "1" || activeTypeTab == "2") {
                    return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.SUCCESSFUL, CERTIFICATE_STATUS.REJECTED].includes(option.value));
                }
                break;
            case CERTIFICATE_STATUS.NO_RESPONSE:
                if (activeTypeTab == "3" || activeTypeTab == "4") {
                    return getStatusOptions(CERTIFICATE_STATUS).filter((option) =>
                        [CERTIFICATE_STATUS.PENDING_FOR_BOARD_DECISION, CERTIFICATE_STATUS.ACCEPTED, CERTIFICATE_STATUS.SUCCESSFUL, CERTIFICATE_STATUS.READY_FOR_ASSESSMENT].includes(option.value)
                    );
                } else if (activeTypeTab == "1" || activeTypeTab == "2") {
                    return getStatusOptions(CERTIFICATE_STATUS).filter((option) =>
                        [CERTIFICATE_STATUS.PENDING_FOR_BOARD_DECISION, CERTIFICATE_STATUS.ACCEPTED, CERTIFICATE_STATUS.SUCCESSFUL].includes(option.value)
                    );
                }
                break;
            case CERTIFICATE_STATUS.PENDING:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) =>
                    [CERTIFICATE_STATUS.ACCEPTED, CERTIFICATE_STATUS.SELFCHECK, CERTIFICATE_STATUS.INCOMPLETE_DOCUMENT].includes(option.value)
                );
            case CERTIFICATE_STATUS.SELFCHECK:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) =>
                    [
                        CERTIFICATE_STATUS.SUCCESSFUL,
                        CERTIFICATE_STATUS.REJECTED,
                        CERTIFICATE_STATUS.PENDING,
                        CERTIFICATE_STATUS.SELFCHECK_SUBMITTED,
                        CERTIFICATE_STATUS.PENDING_FOR_BOARD_DECISION,
                    ].includes(option.value)
                );
            case CERTIFICATE_STATUS.INCOMPLETE_DOCUMENT:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.PENDING_FOR_BOARD_DECISION, CERTIFICATE_STATUS.NO_RESPONSE].includes(option.value));
            case CERTIFICATE_STATUS.ASSESSMENT_SCHEDULED:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.PENDING_FOR_BOARD_DECISION].includes(option.value));
            case CERTIFICATE_STATUS.READY_FOR_ASSESSMENT:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.READY_FOR_ASSESSMENT].includes(option.value));
            case CERTIFICATE_STATUS.ASSESSMENT_FAILED:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.ASSESSMENT_FAILED, CERTIFICATE_STATUS.ASSESSMENT_SUCCESSFUL].includes(option.value));
            case CERTIFICATE_STATUS.ASSESSMENT_SUCCESSFUL:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.ASSESSMENT_FAILED, CERTIFICATE_STATUS.ASSESSMENT_SUCCESSFUL].includes(option.value));
            case CERTIFICATE_STATUS.ADDITIONAL_REVIEW_CHANGE_FIELD:
            case CERTIFICATE_STATUS.ADDITIONAL_REVIEW_ASSESSMENT:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.ASSESSMENT_SUCCESSFUL, CERTIFICATE_STATUS.ASSESSMENT_FAILED].includes(option.value));
            case CERTIFICATE_STATUS.SUCCESSFUL:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.SUCCESSFUL, CERTIFICATE_STATUS.REJECTED].includes(option.value));
            case CERTIFICATE_STATUS.REJECTED:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.SUCCESSFUL, CERTIFICATE_STATUS.REJECTED].includes(option.value));
            case CERTIFICATE_STATUS.REFUND:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.REFUND_SUCCESS].includes(option.value));
            case CERTIFICATE_STATUS.DEREGISTERED:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.DEREGISTERED].includes(option.value));
            case CERTIFICATE_STATUS.REFUND:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.REFUND].includes(option.value));
            case CERTIFICATE_STATUS.REFUND_SUCCESS:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.REFUND_SUCCESS].includes(option.value));
            case CERTIFICATE_STATUS.SELFCHECK_SUBMITTED:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.PENDING_FOR_BOARD_DECISION].includes(option.value));
            case CERTIFICATE_STATUS.PENDING_FOR_BOARD_DECISION:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) => [CERTIFICATE_STATUS.SUCCESSFUL, CERTIFICATE_STATUS.REJECTED].includes(option.value));
            case CERTIFICATE_STATUS.ACCEPTED:
                return getStatusOptions(CERTIFICATE_STATUS).filter((option) =>
                    [CERTIFICATE_STATUS.SUCCESSFUL, CERTIFICATE_STATUS.REJECTED, CERTIFICATE_STATUS.PENDING_FOR_BOARD_DECISION].includes(option.value)
                );
            default:
                return getStatusOptions(CERTIFICATE_STATUS);
        }
    };

    const statusOptions = getFilteredOptions();

    return (
        <div>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize={true}>
                {({ setFieldValue, values, errors }) => {
                    return (
                        <Form className="stepper-form-container">
                            <FormInputLayout label="Status" formikName="status" required={false}>
                                <Select
                                    options={statusOptions}
                                    styles={colourStylesFullWidth}
                                    onChange={(selectedOption) => setFieldValue("status", selectedOption.value)}
                                // value={showAllStatus.find((option) => option.value === firstSelectedStatus)}
                                />
                                <ErrorMessage name="status" component="div" className="text-danger fs-14 fw-400 mt-1" />
                            </FormInputLayout>

                            {values.status === CERTIFICATE_STATUS.SUCCESSFUL && (
                                <FormInputLayout label="Board Approval Date" formikName="approval_date" required={false}>
                                    <DatePicker
                                        value={values.approval_date ? moment(values.approval_date).toDate() : null}
                                        onChange={(date) => {
                                            const formattedDate = date ? moment(date.toDate()).format("YYYY-MM-DD") : "";
                                            setFieldValue("approval_date", formattedDate);
                                        }}
                                        placeholderText="Approval Date"
                                        format="DD MMMM YYYY"
                                        months={["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]}
                                    />
                                    <ErrorMessage name="approval_date" component="div" className="text-danger fs-14 fw-400 mt-1" />
                                </FormInputLayout>
                            )}

                            {values.status === CERTIFICATE_STATUS.REFUND_SUCCESS && (
                                <FormInputLayout label="Refund Date" formikName="refund_date" required={false}>
                                    <DatePicker
                                        value={values.refund_date ? moment(values.refund_date).toDate() : null}
                                        onChange={(date) => {
                                            const formattedDate = date ? moment(date.toDate()).toISOString() : "";
                                            setFieldValue("refund_date", formattedDate);
                                        }}
                                        placeholderText="Refund Date"
                                        format="DD MMMM YYYY"
                                        months={["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]}
                                    />
                                    <ErrorMessage name="refund_date" component="div" className="text-danger fs-14 fw-400 mt-1" />
                                </FormInputLayout>
                            )}

                            {values.status === CERTIFICATE_STATUS.REJECTED && (
                                <FormInputLayout label="Reason" formikName="reason">
                                    <Select
                                        isSearchable={false}
                                        styles={colourStylesFullWidth}
                                        options={rejected_reasons_label_values} // Make sure this variable is defined
                                        components={{
                                            IndicatorSeparator: () => null,
                                        }}
                                        onChange={(selectedOption) => {
                                            const selected = selectedOption?.value;
                                            setFieldValue("reason", selected);
                                            if (selected !== REJECTED_REASON.OTHERS_SECRETARIAT_CAN_WRITE_THE_REASON_MANUALLY.toString()) {
                                                setFieldValue("other_reason", "");
                                            }
                                        }}
                                        value={rejected_reasons_label_values.find((option) => option?.value === values.reason)}
                                    />
                                    <ErrorMessage name="reason" component="div" className="text-danger fs-14 fw-400 mt-1" />
                                </FormInputLayout>
                            )}

                            {values.reason === REJECTED_REASON.OTHERS_SECRETARIAT_CAN_WRITE_THE_REASON_MANUALLY && (
                                <FormInputLayout label="Other Reason" formikName="other_reason">
                                    <Field as="textarea" name="other_reason" className="form-control" placeholder="Please specify the other reason" />
                                    <ErrorMessage name="other_reason" component="div" className="text-danger fs-14 fw-400 mt-1" />
                                </FormInputLayout>
                            )}

                            {values.status === CERTIFICATE_STATUS.REJECTED && (
                                <FormInputLayout label="Board Rejection Date" formikName="appeal_board_date" required={false}>
                                    <DatePicker
                                        value={values.appeal_board_date ? moment(values.appeal_board_date).toDate() : null}
                                        onChange={(date) => {
                                            const formattedDate = date ? moment(date.toDate()).toISOString() : "";
                                            setFieldValue("appeal_board_date", formattedDate);
                                        }}
                                        placeholderText="Appeal Board Date"
                                        format="DD MMMM YYYY"
                                        months={["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]}
                                    />
                                    <ErrorMessage name="appeal_board_date" component="div" className="text-danger fs-14 fw-400 mt-1" />
                                </FormInputLayout>
                            )}

                            {/* Add the "Reason" field for secretariat if status is INCOMPLETE_DOCUMENT */}
                            {values.status === CERTIFICATE_STATUS.INCOMPLETE_DOCUMENT && (
                                <FormInputLayout label="Secretariat Reason" formikName="secretariat_reason">
                                    <Select
                                        isSearchable={false}
                                        styles={colourStylesFullWidth}
                                        options={SecretariatReason}
                                        components={{
                                            IndicatorSeparator: () => null,
                                        }}
                                        value={SecretariatReason.find((option) => option.value === values.secretariat_reason)}
                                        onChange={(selectedOption) => {
                                            setFieldValue("secretariat_reason", selectedOption.value); // Update form state
                                        }}
                                    />
                                    <ErrorMessage name="secretariat_reason" component="div" className="text-danger fs-14 fw-400 mt-1" />
                                </FormInputLayout>
                            )}

                            <div className="d-flex justify-content-end gap-4">
                                <button className="cancel-btn" onClick={closePopup}>
                                    Cancel
                                </button>
                                <PlainButton type="" isLoading={loading}>
                                    Confirm
                                </PlainButton>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
}

export default BulkStatusApplicantUpdate;
